import { useCallback, useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Loader2, LogOut, Phone, RefreshCw, Trash2 } from "lucide-react";
import { api, authHeaders, clearToken, formatApiError, getToken, setToken } from "@/lib/api";
import { usePageMeta } from "@/lib/usePageMeta";

const STATUSES = ["new", "contacted", "quoted", "closed"];

const STATUS_STYLES = {
  new: "bg-amber-100 text-amber-800",
  contacted: "bg-sky-100 text-sky-800",
  quoted: "bg-emerald-100 text-emerald-800",
  closed: "bg-slate-200 text-slate-600",
};

function Login({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setToken(data.token);
      onLogin(data.user);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#0C2E3A] px-4" data-testid="admin-login-page">
      <motion.form
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
        onSubmit={submit}
        className="w-full max-w-sm bg-white p-8 shadow-2xl"
        data-testid="admin-login-form"
      >
        <img src="/assets/logo.jpeg" alt="A&F Insurance Group, Inc. logo" className="mb-6 h-14 w-auto" />
        <h1 className="font-heading text-xl font-bold text-slate-900">Admin Sign In</h1>
        <p className="mt-1 text-sm text-slate-500">Quote submissions dashboard</p>
        <label htmlFor="admin-email" className="mb-1.5 mt-6 block text-xs font-bold uppercase tracking-wider text-slate-700">
          Email
        </label>
        <input
          id="admin-email"
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          autoComplete="username"
          className="w-full border border-slate-300 px-4 py-3 text-sm focus:border-[#0F4C5C] focus:outline-none focus:ring-2 focus:ring-[#0F4C5C]/20"
          data-testid="admin-login-email-input"
        />
        <label htmlFor="admin-password" className="mb-1.5 mt-4 block text-xs font-bold uppercase tracking-wider text-slate-700">
          Password
        </label>
        <input
          id="admin-password"
          type="password"
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          autoComplete="current-password"
          className="w-full border border-slate-300 px-4 py-3 text-sm focus:border-[#0F4C5C] focus:outline-none focus:ring-2 focus:ring-[#0F4C5C]/20"
          data-testid="admin-login-password-input"
        />
        {error && (
          <p role="alert" className="mt-4 border border-red-200 bg-red-50 px-3 py-2 text-sm font-medium text-red-700" data-testid="admin-login-error">
            {error}
          </p>
        )}
        <button
          type="submit"
          disabled={loading}
          className="mt-6 flex w-full items-center justify-center gap-2 bg-[#0F4C5C] py-3 text-sm font-bold uppercase tracking-wider text-white transition-colors duration-200 hover:bg-[#1A365D] disabled:opacity-60"
          data-testid="admin-login-submit-btn"
        >
          {loading && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
          {loading ? "Signing in…" : "Sign In"}
        </button>
      </motion.form>
    </div>
  );
}

function Dashboard({ user, onLogout }) {
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get("/quotes", { headers: authHeaders() });
      setQuotes(data.quotes);
    } catch (err) {
      if (err?.response?.status === 401) {
        clearToken();
        onLogout();
        return;
      }
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, [onLogout]);

  useEffect(() => {
    load();
  }, [load]);

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/quotes/${id}`, { status }, { headers: authHeaders() });
      setQuotes((qs) => qs.map((q) => (q.id === id ? { ...q, status } : q)));
    } catch (err) {
      setError(formatApiError(err));
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/quotes/${id}`, { headers: authHeaders() });
      setQuotes((qs) => qs.filter((q) => q.id !== id));
    } catch (err) {
      setError(formatApiError(err));
    }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="admin-dashboard">
      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3">
            <img src="/assets/logo.jpeg" alt="A&F Insurance Group, Inc. logo" className="h-11 w-auto" />
            <div>
              <h1 className="font-heading text-lg font-bold text-slate-900">Quote Submissions</h1>
              <p className="text-xs text-slate-500">Signed in as {user.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={load}
              className="flex items-center gap-1.5 border border-slate-300 px-3 py-2 text-xs font-bold uppercase tracking-wider text-slate-700 hover:bg-slate-50"
              data-testid="admin-refresh-btn"
            >
              <RefreshCw className="h-3.5 w-3.5" aria-hidden="true" /> Refresh
            </button>
            <button
              type="button"
              onClick={() => { clearToken(); onLogout(); }}
              className="flex items-center gap-1.5 bg-[#0F4C5C] px-3 py-2 text-xs font-bold uppercase tracking-wider text-white hover:bg-[#1A365D]"
              data-testid="admin-logout-btn"
            >
              <LogOut className="h-3.5 w-3.5" aria-hidden="true" /> Sign Out
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        {error && (
          <p role="alert" className="mb-4 border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-700" data-testid="admin-error-message">
            {error}
          </p>
        )}
        {loading ? (
          <div className="flex items-center justify-center py-24" data-testid="admin-loading">
            <Loader2 className="h-8 w-8 animate-spin text-[#0F4C5C]" aria-label="Loading quotes" />
          </div>
        ) : quotes.length === 0 ? (
          <div className="border border-dashed border-slate-300 bg-white py-24 text-center" data-testid="admin-empty-state">
            <p className="font-heading text-lg font-bold text-slate-700">No quote requests yet</p>
            <p className="mt-1 text-sm text-slate-500">New submissions from the website quote form will appear here.</p>
          </div>
        ) : (
          <div className="overflow-x-auto border border-slate-200 bg-white" data-testid="admin-quote-submissions-table">
            <table className="w-full min-w-[900px] text-left text-sm">
              <thead>
                <tr className="border-b border-slate-200 bg-[#EEF6FC] text-xs font-bold uppercase tracking-wider text-[#0F4C5C]">
                  <th scope="col" className="px-4 py-3">Received</th>
                  <th scope="col" className="px-4 py-3">Name</th>
                  <th scope="col" className="px-4 py-3">Contact</th>
                  <th scope="col" className="px-4 py-3">Insurance Type</th>
                  <th scope="col" className="px-4 py-3">Message</th>
                  <th scope="col" className="px-4 py-3">Status</th>
                  <th scope="col" className="px-4 py-3"><span className="sr-only">Actions</span></th>
                </tr>
              </thead>
              <tbody>
                {quotes.map((q) => (
                  <tr key={q.id} className="border-b border-slate-100 align-top hover:bg-slate-50" data-testid={`quote-row-${q.id}`}>
                    <td className="whitespace-nowrap px-4 py-3 text-xs text-slate-500">
                      {new Date(q.created_at).toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" })}
                    </td>
                    <td className="px-4 py-3 font-semibold text-slate-900">{q.name}</td>
                    <td className="px-4 py-3">
                      <a href={`mailto:${q.email}`} className="block text-[#0F4C5C] hover:underline">{q.email}</a>
                      <a href={`tel:${q.phone}`} className="mt-0.5 flex items-center gap-1 text-xs text-slate-500 hover:text-[#0F4C5C]">
                        <Phone className="h-3 w-3" aria-hidden="true" /> {q.phone}
                      </a>
                    </td>
                    <td className="px-4 py-3 font-medium text-slate-700">{q.insurance_type}</td>
                    <td className="max-w-xs px-4 py-3 text-xs leading-relaxed text-slate-600">{q.message || "-"}</td>
                    <td className="px-4 py-3">
                      <select
                        value={q.status}
                        onChange={(e) => updateStatus(q.id, e.target.value)}
                        aria-label={`Status for quote from ${q.name}`}
                        className={`border-0 px-2 py-1 text-xs font-bold uppercase tracking-wider ${STATUS_STYLES[q.status] || STATUS_STYLES.new}`}
                        data-testid={`quote-status-select-${q.id}`}
                      >
                        {STATUSES.map((s) => (
                          <option key={s} value={s}>{s}</option>
                        ))}
                      </select>
                    </td>
                    <td className="px-4 py-3">
                      <button
                        type="button"
                        onClick={() => remove(q.id)}
                        aria-label={`Delete quote from ${q.name}`}
                        className="p-1.5 text-slate-400 hover:text-red-600"
                        data-testid={`quote-delete-btn-${q.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>
    </div>
  );
}

export default function Admin() {
  usePageMeta("Admin | A&F Insurance Group");
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    const verify = async () => {
      if (!getToken()) {
        setChecking(false);
        return;
      }
      try {
        const { data } = await api.get("/auth/me", { headers: authHeaders() });
        setUser(data);
      } catch {
        clearToken();
      } finally {
        setChecking(false);
      }
    };
    verify();
  }, []);

  if (checking) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#0C2E3A]" data-testid="admin-checking">
        <Loader2 className="h-8 w-8 animate-spin text-white" aria-label="Checking session" />
      </div>
    );
  }

  return user ? <Dashboard user={user} onLogout={() => setUser(null)} /> : <Login onLogin={setUser} />;
}
